import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import { talentService } from '../../../api/apiService';
import InfoTip from '../../InfoTip';
import { customStyles } from '../../../helpers/StyleUtils';
import { buildAgencies, findAgencyOf, agencyPayload, clearedAgencyPayload } from '../../../helpers/agencies';
import '../../../App.css';
import './TalentEditSimple.css';

// Special choices in the agency dropdown, next to the existing agencies
const AGENCY_NONE = '__none__';
const AGENCY_NEW = '__new__';

const TAX_TIP = "Der hier angegebene Steuersatz wird automatisch in deine Gutschrift übernommen.";
const VAT_TIP = "Info – in Deutschland genügt entweder die\n• Angabe der Steuernummer oder der Umsatzsteuer-ID. Eine von beiden ist jedoch Pflicht.";

/**
 * Edit talent for the simple list only: basic info + Agentur, AP, Gage, Lano ID + VAT/billing.
 * No related talents, no good/tryout/bad company relationships.
 */
function TalentEditSimple() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [talent, setTalent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Existing agencies (derived from all talents, see helpers/agencies.js) for the dropdown.
  // 'existing' = picked from the list, 'new' = typing a name that does not exist yet.
  const [agencies, setAgencies] = useState([]);
  const [agenciesLoading, setAgenciesLoading] = useState(true);
  const [agencyMode, setAgencyMode] = useState('existing');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await talentService.getTalentsList(1, 5000, '');
        if (!cancelled) setAgencies(buildAgencies(res.data?.talents || []));
      } catch (err) {
        console.error('Error loading agencies:', err);
      } finally {
        if (!cancelled) setAgenciesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const fetchTalent = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const response = await talentService.getTalentById(id);
        setTalent(response.data);
      } catch (err) {
        console.error('Error fetching talent:', err);
        setError('Failed to load talent');
      } finally {
        setLoading(false);
      }
    };
    fetchTalent();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    const next = type === 'checkbox' ? e.target.checked : value;
    setTalent(prev => {
      const updated = { ...prev, [name]: next };
      // Mutually exclusive billing modes: separate CNs vs. agency bills on own account
      if (name === 'agency_bill_separately' && next) updated.agency_own_account = false;
      if (name === 'agency_own_account' && next) updated.agency_bill_separately = false;
      return updated;
    });
  };

  // ── Agency dropdown ──────────────────────────────────────────────────────────
  const agencyOptions = [
    { value: AGENCY_NONE, label: 'No agency' },
    ...agencies.map((a) => ({ value: a.key, label: `${a.name} (${a.talents.length})`, agency: a })),
    { value: AGENCY_NEW, label: '+ New agency…' },
  ];
  const currentAgency = talent ? findAgencyOf(agencies, { Agentur: talent.agentur ?? talent.Agentur }) : null;
  const agencySelectValue = agencyMode === 'new'
    ? agencyOptions.find((o) => o.value === AGENCY_NEW)
    : currentAgency
      ? agencyOptions.find((o) => o.value === currentAgency.key)
      : (talent && (talent.agentur || talent.Agentur) ? agencyOptions.find((o) => o.value === AGENCY_NEW) : agencyOptions[0]);

  /**
   * Picking an existing agency copies its exact name and all agency fields onto the form, so
   * this talent ends up with the same data as the other members (and the same spelling, which
   * is what keeps it in that agency's group). "No agency" clears them; "New agency" opens a
   * name field and leaves the agency fields for typing.
   */
  const handleAgencyPick = (option) => {
    if (!option || option.value === AGENCY_NONE) {
      setAgencyMode('existing');
      setTalent((prev) => ({ ...prev, ...clearedAgencyPayload(), agentur: '', Agentur: '' }));
      return;
    }
    if (option.value === AGENCY_NEW) {
      setAgencyMode('new');
      setTalent((prev) => ({ ...prev, agentur: '', Agentur: '' }));
      return;
    }
    setAgencyMode('existing');
    const payload = agencyPayload(option.agency);
    setTalent((prev) => ({ ...prev, ...payload, agentur: payload.Agentur, ap: payload.AP }));
  };

  // Rate fields (day rate / hourly rate): numbers with up to two decimals
  // (e.g. 185.65). A typed comma is normalised to a dot.
  const handleRateChange = (e) => {
    const { name } = e.target;
    const value = e.target.value.replace(',', '.');
    if (/^\d*\.?\d{0,2}$/.test(value)) {
      setTalent(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveMessage('');
    try {
      const original = await talentService.getTalentById(id).then(r => r.data);
      const payload = {};
      const set = (key, newVal, origVal) => {
        if (newVal !== origVal) payload[key] = newVal;
      };
      // First 4 fields are read-only, not sent on save
      set('Tagessatz', talent.dayRate || talent.Tagessatz, original.Tagessatz);
      set('Stundensatz', talent.hourlyRate || talent.Stundensatz, original.Stundensatz);
      set('Telefon', talent.phone || talent.Telefon, original.Telefon);
      set('Notiz_Setcast', talent.notes || talent.Notiz_Setcast, original.Notiz_Setcast);
      set('Agentur', talent.agentur || talent.Agentur, original.Agentur);
      set('AP', talent.ap != null ? talent.ap : talent.AP, original.AP);
   //   set('Gage', talent.gage || talent.Gage, original.Gage);
      set('Lano_ID', talent.lanoId || talent.Lano_ID, original.Lano_ID);
      set('vat_status', talent.vat_status, original.vat_status);
      set('tax_id', talent.tax_id, original.tax_id);
      set('vat_id', talent.vat_id, original.vat_id);
      set('bank_account', talent.bank_account, original.bank_account);
      set('country', talent.country, original.country);
      set('company_name', talent.company_name, original.company_name);
      set('agency_vat_status', talent.agency_vat_status, original.agency_vat_status);
      set('agency_tax_id', talent.agency_tax_id, original.agency_tax_id);
      set('agency_vat_id', talent.agency_vat_id, original.agency_vat_id);
      set('agency_bank_account', talent.agency_bank_account, original.agency_bank_account);
      set('agency_email', talent.agency_email, original.agency_email);
      set('agency_address', talent.agency_address, original.agency_address);
      set('address', talent.address, original.address);
      set('is_agency', !!(talent.is_agency === true || talent.is_agency === 1), !!(original.is_agency === true || original.is_agency === 1));
      set('agency_bill_separately', !!(talent.agency_bill_separately === true || talent.agency_bill_separately === 1), !!(original.agency_bill_separately === true || original.agency_bill_separately === 1));
      set('agency_own_account', !!(talent.agency_own_account === true || talent.agency_own_account === 1), !!(original.agency_own_account === true || original.agency_own_account === 1));

      if (Object.keys(payload).length > 0) {
        await talentService.updateTalent(id, payload);
        setSaveMessage('Saved.');
      } else {
        setSaveMessage('No changes.');
      }
    } catch (err) {
      console.error('Error saving:', err);
      setError('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) return <div className="content-wrapper page"><div className="content-wrapper"><p>Loading…</p></div></div>;
  if (error) return <div className="content-wrapper page"><div className="content-wrapper"><p className="error">{error}</p></div></div>;
  if (!talent) return <div className="content-wrapper page"><div className="content-wrapper"><p className="error">Talent not found</p></div></div>;

  return (
    <div className="content-wrapper page">
      <div className="content-wrapper">
        <h1>Edit talent (billing)</h1>
        {saveMessage && (
          <div className="talent-edit-simple-message success">
            {saveMessage}
          </div>
        )}
        <form onSubmit={handleSave}>
          <h2>Basic information</h2>
          <div className="form-group">
            <label>Name</label>
            <input type="text" readOnly value={talent.name || talent.Name || ''} className="talent-edit-readonly" />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input type="email" readOnly value={talent.email || talent.E_Mail || ''} className="talent-edit-readonly" />
          </div>
          <div className="form-group">
            <label>Function</label>
            <input type="text" readOnly value={talent.function || talent.Funktion || ''} className="talent-edit-readonly" />
          </div>
          <div className="form-group">
            <label>Type</label>
            <input type="text" readOnly value={talent.type || talent.Bereich || ''} className="talent-edit-readonly" />
          </div>
          <div className="form-group">
            <label>Day rate</label>
            <input type="text" inputMode="decimal" name="dayRate" value={talent.dayRate || talent.Tagessatz || ''} onChange={handleRateChange} required />
          </div>
          <div className="form-group">
            <label>Hourly rate</label>
            <input type="text" inputMode="decimal" name="hourlyRate" value={talent.hourlyRate || talent.Stundensatz || ''} onChange={handleRateChange} />
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input type="text" name="phone" value={talent.phone || talent.Telefon || ''} onChange={handleInputChange} />
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea name="notes" value={talent.notes || talent.Notiz_Setcast || ''} onChange={handleInputChange} rows={4} />
          </div>

          <h2 className="talent-edit-section-title">Agency / AP</h2>
          <div className="form-group talent-edit-checkbox">
            <label>
              <input
                type="checkbox"
                name="is_agency"
                checked={!!(talent.is_agency === true || talent.is_agency === 1)}
                onChange={handleInputChange}
              />
              {' '}Is agency
            </label>
            <small className="talent-edit-hint">
              When checked, this talent is treated as an agency: agency info and VAT are used on credit notes. You can bill all in one CN or separately (talent CN + agency provision CN).
            </small>
          </div>
          {(talent.is_agency === true || talent.is_agency === 1) && (
            <>
              <div className="form-group talent-edit-checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="agency_bill_separately"
                    checked={!!(talent.agency_bill_separately === true || talent.agency_bill_separately === 1)}
                    onChange={handleInputChange}
                  />
                  {' '}Bill separately (talent CN + agency provision CN)
                </label>
                <small className="talent-edit-hint">
                  When unchecked (default, client-approved): ONE credit note to the agency — talent lines with talent VAT plus the AP provision position with agency VAT (mixed rates supported, tax IDs shown per line). When checked: two linked credit notes (talent + agency provision) — only for agencies that explicitly require separate documents.
                </small>
              </div>
              <div className="form-group talent-edit-checkbox">
                <label>
                  <input
                    type="checkbox"
                    name="agency_own_account"
                    checked={!!(talent.agency_own_account === true || talent.agency_own_account === 1)}
                    onChange={handleInputChange}
                  />
                  {' '}Agency bills on its own account (Agentur rechnet auf eigene Rechnung ab)
                </label>
                <small className="talent-edit-hint">
                  When checked, the credit note texts no longer say the agency acts &quot;im Namen und im Auftrag des Talents&quot; — the agency bills the talent&apos;s services in its own name and for its own account. Mutually exclusive with &quot;Bill separately&quot;.
                </small>
              </div>
            </>
          )}
          <small className="talent-edit-hint talent-edit-hint-block">
            Pick the agency from the list: its name, VAT, bank and address data are copied onto this talent so all talents of an agency match. Use &quot;New agency&quot; only when the agency does not exist yet. Agency-wide changes are made under Agency Management.
          </small>
          <div className="form-group">
            <label>Agency</label>
            <Select
              inputId="talent-agency"
              value={agencySelectValue || null}
              onChange={handleAgencyPick}
              options={agencyOptions}
              isLoading={agenciesLoading}
              placeholder={agenciesLoading ? 'Loading agencies…' : 'Select agency'}
              styles={customStyles}
            />
            {(agencyMode === 'new' || (!currentAgency && (talent.agentur || talent.Agentur))) && (
              <>
                <input
                  type="text"
                  name="agentur"
                  value={talent.agentur || talent.Agentur || ''}
                  onChange={handleInputChange}
                  placeholder="New agency name"
                  style={{ marginTop: '8px' }}
                />
                <small className="talent-edit-hint">
                  {currentAgency ? '' : 'This name is not in the agency list yet; fill in the agency fields below. Check the list first to avoid a second spelling of an existing agency.'}
                </small>
              </>
            )}
          </div>
          <div className="form-group">
            <label>AP</label>
            <input type="text" name="ap" value={talent.ap != null ? talent.ap : (talent.AP ?? '')} onChange={handleInputChange} />
          </div>
 
          <div className="form-group">
            <label>Lano ID</label>
            <input type="text" name="lanoId" value={talent.lanoId || talent.Lano_ID || ''} onChange={handleInputChange} />
          </div>
          <div className="form-group">
            <label>Agency VAT status <InfoTip text={TAX_TIP} /></label>
            <select name="agency_vat_status" value={talent.agency_vat_status || ''} onChange={handleInputChange}>
              <option value="">—</option>
              <option value="with_vat">With VAT (19 %)</option>
              <option value="with_vat_7">With VAT (7 %)</option>
              <option value="without_vat">Without VAT</option>
            </select>
            <small className="talent-edit-hint">
              For agency/divided credit notes. Set &quot;With VAT&quot; when agency charges 19 %.
            </small>
          </div>
          <div className="form-group">
            <label>Agency Tax Number / Steuer Nummer (xxx/xxx/xxx) <InfoTip text={VAT_TIP} /></label>
            <input type="text" name="agency_tax_id" value={talent.agency_tax_id || ''} onChange={handleInputChange} placeholder="xxx/xxx/xxx" />
          </div>
          <div className="form-group">
            <label>Agency VAT ID / Ust-Nr (DE XXXXXXXXX) <InfoTip text={VAT_TIP} /></label>
            <input type="text" name="agency_vat_id" value={talent.agency_vat_id || ''} onChange={handleInputChange} placeholder="DE XXXXXXXXX" />
          </div>
          <div className="form-group">
            <label>Agency bank account</label>
            <textarea name="agency_bank_account" value={talent.agency_bank_account || ''} onChange={handleInputChange} rows={2} placeholder="Bank Name, IBAN, BIC" />
          </div>
          <div className="form-group">
            <label>Agency email</label>
            <input type="email" name="agency_email" value={talent.agency_email || ''} onChange={handleInputChange} />
          </div>
          <div className="form-group">
            <label>Agency address</label>
            <textarea name="agency_address" value={talent.agency_address || ''} onChange={handleInputChange} rows={2} placeholder="Straße Hausnr., PLZ Ort, Land" />
            <small className="talent-edit-hint">Required on credit notes (German law). E.g.: Musterstraße 12, 20099 Hamburg</small>
          </div>

          <h2 className="talent-edit-section-title">Billing / VAT</h2>
          <div className="form-group">
            <label>VAT status <InfoTip text={TAX_TIP} /></label>
            <select name="vat_status" value={talent.vat_status || ''} onChange={handleInputChange}>
              <option value="">—</option>
              <option value="with_vat">With VAT (19 %)</option>
              <option value="with_vat_7">With VAT (7 %)</option>
              <option value="without_vat">Without VAT</option>
              <option value="kleinunternehmer">Kleinunternehmer</option>
              <option value="auslander">Ausländer (abroad)</option>
            </select>
            <small className="talent-edit-hint">
              When talent has no VAT and &quot;Agency VAT status&quot; above is With VAT (and AP is set), two credit notes are created automatically (talent + agency). With &quot;Is agency&quot; + &quot;Bill separately&quot; you get talent CN + agency provision CN.
            </small>
          </div>
          <div className="form-group">
            <label>Tax Number / Steuer Nummer (xxx/xxx/xxx) <InfoTip text={VAT_TIP} /></label>
            <input type="text" name="tax_id" value={talent.tax_id || ''} onChange={handleInputChange} placeholder="xxx/xxx/xxx" />
          </div>
          <div className="form-group">
            <label>VAT ID / Ust-Nr (DE XXXXXXXXX) <InfoTip text={VAT_TIP} /></label>
            <input type="text" name="vat_id" value={talent.vat_id || ''} onChange={handleInputChange} placeholder="DE XXXXXXXXX" />
          </div>
          <div className="form-group">
            <label>Bank account</label>
            <textarea name="bank_account" value={talent.bank_account || ''} onChange={handleInputChange} rows={2} placeholder="Bank Name, IBAN, BIC" />
          </div>
          <div className="form-group">
            <label>Country</label>
            <input type="text" name="country" value={talent.country || ''} onChange={handleInputChange} placeholder="e.g. Germany" />
          </div>
          <div className="form-group">
            <label>Company name</label>
            <input type="text" name="company_name" value={talent.company_name || ''} onChange={handleInputChange} />
          </div>
          <div className="form-group">
            <label>Address (Adresse)</label>
            <textarea name="address" value={talent.address || ''} onChange={handleInputChange} rows={2} placeholder="Straße Hausnr., PLZ Ort, Land" />
            <small className="talent-edit-hint">Required on credit notes (German law). E.g.: Musterstraße 12, 20099 Hamburg</small>
          </div>

          <div className="button-group talent-edit-actions">
            <button type="submit" className="button primary" disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="button secondary"
              onClick={() => navigate('/admin/talent-list-simple')}
              disabled={isSaving}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TalentEditSimple;
